/* Admin — Editor Seats panel (Feb-2026).
 *
 * Wraps `GET /admin/credentials/full` + `POST/PUT/DELETE
 * /admin/credentials*` so pro_writer admins can:
 *   • create a new admin user with an email + starter scopes,
 *   • toggle each scope on the row (pro_writer/bookstore/coach/editor),
 *   • edit the row's email (used by the review digest),
 *   • delete a custom user (seeded accounts are refused).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, RefreshCw, Save, ShieldCheck, Mail, User,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

import { openConfirm } from '@/components/admin/ConfirmDialog';

const SCOPE_META = {
  pro_writer: { label: 'The Loom Admin',       tone: 'text-oxblood-700 bg-oxblood-500/10' },
  bookstore:  { label: 'Bookstore Admin',      tone: 'text-amber-800 bg-amber-500/10'    },
  coach:      { label: 'Premier Coach',        tone: 'text-sky-800 bg-sky-500/10'        },
  editor:     { label: 'Reader-Review Editor', tone: 'text-emerald-800 bg-emerald-500/10' },
};

export default function AdminSeatsPanel() {
  const [rows, setRows]         = useState([]);
  const [validScopes, setValid] = useState(['pro_writer', 'bookstore', 'coach', 'editor']);
  const [seeded, setSeeded]     = useState(['Administrator', 'David']);
  const [loading, setLoading]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft]       = useState({ username: '', password: '', email: '', scopes: ['bookstore'] });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/credentials/full');
      setRows(r.data?.credentials || []);
      setValid(r.data?.valid_scopes || []);
      setSeeded(r.data?.seeded_usernames || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load seats.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!draft.username || !draft.password) {
      toast.error('Username + password are required.');
      return;
    }
    setCreating(true);
    try {
      await http.post('/admin/credentials', {
        username: draft.username,
        password: draft.password,
        email:    draft.email || undefined,
        scopes:   draft.scopes,
      });
      toast.success(`Created ${draft.username}.`);
      setDraft({ username: '', password: '', email: '', scopes: ['bookstore'] });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed.');
    } finally { setCreating(false); }
  };

  return (
    <section className="space-y-4" data-testid="admin-seats-panel">
      {/* Create card */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-semibold">
          <Plus className="h-3.5 w-3.5" /> New admin seat
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <input
            value={draft.username}
            onChange={(e) => setDraft({ ...draft, username: e.target.value })}
            placeholder="Username"
            data-testid="seats-new-username"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-[12px]"
          />
          <input
            type="password" value={draft.password}
            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            placeholder="Password (≥8 chars)"
            data-testid="seats-new-password"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-[12px]"
          />
          <input
            type="email" value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            placeholder="editor@yourdomain.com (optional)"
            data-testid="seats-new-email"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-[12px]"
          />
          <button type="button" onClick={create} disabled={creating}
                  data-testid="seats-create-btn"
                  className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create seat
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {validScopes.map((s) => (
            <label key={s}
                   className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-wider ${
                     draft.scopes.includes(s)
                       ? 'border-primary bg-primary text-primary-foreground'
                       : 'border-border bg-background text-muted-foreground'}`}>
              <input type="checkbox" className="hidden"
                     checked={draft.scopes.includes(s)}
                     onChange={(e) => {
                       const next = new Set(draft.scopes);
                       if (e.target.checked) next.add(s); else next.delete(s);
                       setDraft({ ...draft, scopes: Array.from(next) });
                     }}
                     data-testid={`seats-new-scope-${s}`} />
              {SCOPE_META[s]?.label || s}
            </label>
          ))}
        </div>
      </div>

      {/* Roster */}
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="text-[13px] font-semibold">Editor seats ({rows.length})</div>
          <button onClick={refresh}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary"
                  data-testid="seats-refresh-btn">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
        </div>
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <SeatRow key={r.username} row={r}
                     seeded={seeded.includes(r.username)}
                     validScopes={validScopes}
                     onChanged={refresh} />
          ))}
          {rows.length === 0 && (
            <li className="px-3 py-4 text-center text-[11.5px] text-muted-foreground">No admin seats yet.</li>
          )}
        </ul>
      </div>
    </section>
  );
}


function SeatRow({ row, seeded, validScopes, onChanged }) {
  const [scopes, setScopes] = useState(row.scopes || []);
  const [email,  setEmail]  = useState(row.email || '');
  const [busy,   setBusy]   = useState(false);
  const dirty = useMemo(() => {
    const a = [...(row.scopes || [])].sort().join(',');
    const b = [...scopes].sort().join(',');
    return a !== b || (row.email || '') !== email;
  }, [row, scopes, email]);

  const save = async () => {
    setBusy(true);
    try {
      const jobs = [];
      const a = [...(row.scopes || [])].sort().join(',');
      const b = [...scopes].sort().join(',');
      if (a !== b) {
        jobs.push(http.put(`/admin/credentials/${encodeURIComponent(row.username)}/scopes`,
                             { scopes }));
      }
      if ((row.email || '') !== email) {
        jobs.push(http.put(`/admin/credentials/${encodeURIComponent(row.username)}/email`,
                             { email: email || null }));
      }
      await Promise.all(jobs);
      toast.success(`Updated ${row.username}.`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await openConfirm({
      title: `Delete admin seat ${row.username}?`,
      description: 'The user loses admin scopes immediately. Reinstating requires re-seating them from scratch.',
      confirmLabel: 'Delete seat',
      tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      await http.delete(`/admin/credentials/${encodeURIComponent(row.username)}`);
      toast.success(`Deleted ${row.username}.`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    } finally { setBusy(false); }
  };

  return (
    <li className="px-3 py-3" data-testid={`seat-row-${row.username}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="inline-flex items-center gap-1 text-[13px] font-semibold">
            <User className="h-3.5 w-3.5" /> {row.username}
            {seeded && <span className="ml-2 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-amber-800">Seeded</span>}
            {row.must_rotate && <span className="ml-2 rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-rose-700">Must rotate</span>}
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            last login {(row.last_login_at || 'never').replace('T', ' ').slice(0, 16)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Mail className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="editor@…"
            data-testid={`seat-email-${row.username}`}
            className="w-[220px] rounded-md border border-border bg-background px-2 py-1 text-[11.5px]" />
        </div>
        <div className="flex items-center gap-1">
          <button onClick={save} disabled={!dirty || busy}
                  data-testid={`seat-save-${row.username}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save
          </button>
          {!seeded && (
            <button onClick={remove} disabled={busy}
                    data-testid={`seat-delete-${row.username}`}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-500/10">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {validScopes.map((s) => {
          const active = scopes.includes(s);
          return (
            <label key={s}
                   data-testid={`seat-scope-${row.username}-${s}`}
                   className={`inline-flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-wider transition ${
                     active
                       ? `border-transparent ${SCOPE_META[s]?.tone || 'bg-primary text-primary-foreground'}`
                       : 'border-border bg-background text-muted-foreground'}`}>
              <input type="checkbox" className="hidden"
                     checked={active}
                     onChange={(e) => {
                       const next = new Set(scopes);
                       if (e.target.checked) next.add(s); else next.delete(s);
                       setScopes(Array.from(next));
                     }} />
              <ShieldCheck className="h-2.5 w-2.5" />
              {SCOPE_META[s]?.label || s}
            </label>
          );
        })}
      </div>
    </li>
  );
}
